/**
 * AdventureFileListView Component
 * Displays files in a table/list view with sortable columns
 */

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  Skeleton,
} from '@mui/material'
import {
  MoreVert,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
} from '@mui/icons-material'
import type { AdventureMediaFileDto } from '@api/types/adventureFiles'
import { formatFileSize, getFileTypeIcon } from '@api/types/adventureFiles'

interface AdventureFileListViewProps {
  files: AdventureMediaFileDto[]
  onFileClick: (file: AdventureMediaFileDto) => void
  onMenuClick: (event: React.MouseEvent<HTMLElement>, file: AdventureMediaFileDto) => void
  isLoading?: boolean
}

function FileTypeAvatar({ file }: { file: AdventureMediaFileDto }) {
  const isImage = file.mimeType.startsWith('image/')
  const type = getFileTypeIcon(file.mimeType)

  // Show thumbnail for images
  if (isImage && (file.thumbnailUrl || file.fileUrl)) {
    return (
      <Avatar
        src={file.thumbnailUrl || file.fileUrl}
        variant="rounded"
        sx={{ width: 40, height: 40 }}
      />
    )
  }

  // Show icon for other types
  const iconMap = {
    image: <ImageIcon />,
    video: <VideoFile />,
    audio: <AudioFile />,
    document: <Description />,
    file: <InsertDriveFile />,
  }

  return (
    <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}>
      {iconMap[type]}
    </Avatar>
  )
}

export function AdventureFileListView({
  files,
  onFileClick,
  onMenuClick,
  isLoading,
}: AdventureFileListViewProps) {
  if (isLoading) {
    return <AdventureFileListViewSkeleton />
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={60}></TableCell>
            <TableCell>Name</TableCell>
            <TableCell align="right" width={100}>Size</TableCell>
            <TableCell align="right" width={100}>Uses</TableCell>
            <TableCell align="right" width={120}>Date</TableCell>
            <TableCell width={48}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => {
            const displayName = file.displayName || file.fileName

            return (
              <TableRow
                key={file.id}
                hover
                onClick={() => onFileClick(file)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <FileTypeAvatar file={file} />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                      {displayName}
                    </Typography>
                    {file.displayName && file.displayName !== file.fileName && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                        {file.fileName}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.fileSize)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {file.usageCount > 0 ? (
                    <Chip
                      label={file.usageCount}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {new Date(file.createdDate).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      onMenuClick(e, file)
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function AdventureFileListViewSkeleton() {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={60}></TableCell>
            <TableCell>Name</TableCell>
            <TableCell align="right" width={100}>Size</TableCell>
            <TableCell align="right" width={100}>Uses</TableCell>
            <TableCell align="right" width={120}>Date</TableCell>
            <TableCell width={48}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton variant="rounded" width={40} height={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="60%" />
              </TableCell>
              <TableCell align="right">
                <Skeleton variant="text" width={50} sx={{ ml: 'auto' }} />
              </TableCell>
              <TableCell align="right">
                <Skeleton variant="text" width={30} sx={{ ml: 'auto' }} />
              </TableCell>
              <TableCell align="right">
                <Skeleton variant="text" width={80} sx={{ ml: 'auto' }} />
              </TableCell>
              <TableCell>
                <Skeleton variant="circular" width={24} height={24} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
